import { toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopRolesTable } from "@/modules/merchant/presentation/ui/ShopRolesTable";
import { ShopRolesToolbar } from "@/modules/merchant/presentation/ui/ShopRolesToolbar";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useDeleteShopRole,
  useSeedDefaultRoles,
  useShopRolesQuery,
} from "../api/queries";

export function ShopRolesPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: "/app/shops/$shopId/roles" });

  const list = useShopRolesQuery(shopId);
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;

  const canCreate = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shop_roles.create,
    verifiedLevel,
  );
  const canUpdate = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shop_roles.update,
    verifiedLevel,
  );

  const canDelete = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shop_roles.delete,
    verifiedLevel,
  );

  const seedDefault = useSeedDefaultRoles(shopId);
  const deleteShopRole = useDeleteShopRole(shopId);

  const items = list.data ?? [];
  const showSeed = items.length === 0 && canCreate;

  return (
    <RequireMerchantPermission permission={MerchantPermissions.shop_roles.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.roles(shopId)} />
      </Header>

      <Main>
        <ShopRolesToolbar
          canCreate={canCreate}
          canSeed={showSeed}
          onSeed={() => seedDefault.mutate()}
          onCreate={() =>
            nav({ to: "/app/shops/$shopId/roles/new", params: { shopId } })
          }
          seedLoading={seedDefault.isPending}
        />

        <div className="flex flex-col">
          <ShopRolesTable
            data={items}
            isLoading={list.isLoading}
            onEdit={(role) =>
              nav({
                to: "/app/shops/$shopId/roles/$roleId/edit",
                params: { shopId, roleId: role.id },
              })
            }
            onDelete={(role) =>
              toast.promise(deleteShopRole.run(role.id), {
                loading: "ກຳລັງລົບ...",
                success: "ລົບບົດບາດສຳເລັດ",
                error: "ລົບບົດບາດລົ້ມເຫຼວ",
              })
            }
            canUpdate={canUpdate}
            canDelete={canDelete}
          />
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
